import type { FocusMessageProjection, ProposalProjection } from "@mind/contracts/browser";

export type FocusDraftMode = "new" | "active";

export type PartialFocusStart = {
  readonly threadId: string;
  readonly branchId: string;
  readonly prompt: string;
};

export type FocusStarterSelection = {
  readonly draft: string;
  readonly shouldSubmit: false;
};

export const FOCUS_STARTER_KEYS = ["plan", "compare", "summarize"] as const;

export function focusDraftKey(workspaceId: string, mode: FocusDraftMode, threadId: string | null): string {
  return `${workspaceId}:${mode === "new" ? "new" : `thread:${threadId ?? "unknown"}`}`;
}

export function writeFocusDraft(
  drafts: Readonly<Record<string, string>>,
  key: string,
  value: string,
): Record<string, string> {
  return { ...drafts, [key]: value };
}

export function seedPartialStartDraft(
  drafts: Readonly<Record<string, string>>,
  workspaceId: string,
  partial: PartialFocusStart,
): Record<string, string> {
  return writeFocusDraft(drafts, focusDraftKey(workspaceId, "active", partial.threadId), partial.prompt);
}

export function selectFocusStarter(prompt: string): FocusStarterSelection {
  return { draft: prompt, shouldSubmit: false };
}

export function shouldShowPendingPrompt(
  pending: { readonly threadId: string; readonly branchId: string; readonly runId: string } | null,
  threadId: string | null,
  branchId: string | undefined,
  messages: readonly FocusMessageProjection[],
): boolean {
  return pending !== null
    && pending.threadId === threadId
    && pending.branchId === branchId
    && !messages.some((message) => message.runId === pending.runId && message.role === "user");
}

export function proposalOperationKey(type: ProposalProjection["command"]["type"]): string {
  switch (type) {
    case "note.create": return "noteCreate";
    case "note.update": return "noteUpdate";
    case "note.archive": return "noteArchive";
    case "project.create": return "projectCreate";
    case "project.update": return "projectUpdate";
    case "task.create": return "taskCreate";
    case "entity_link.create": return "linkCreate";
    case "entity_link.remove": return "linkRemove";
    case "document.patch": return "documentPatch";
    case "document.replace_from_git":
    case "document.replace_recovered":
      return "documentReplace";
    default: return "unknown";
  }
}

export async function copyFocusText(
  text: string,
  writeText: (value: string) => Promise<void>,
): Promise<"copied" | "failed"> {
  try {
    await writeText(text);
    return "copied";
  } catch {
    return "failed";
  }
}
